import styled from 'foundations-theming/utils';
import { fwcBorderRadius } from 'foundations-theming/utils/fwc';
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { Button } from '../Button/Button';
import { callIfPossible } from '../../utils/functions';
import { mergeRefs } from '../../utils/mergeRefs/mergeRefs';
import { Controllable } from '../Controllable';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { lazyEval } from '../../utils/lazyEval';
import { Overhang as DefaultOverhang } from '../Overhang/Overhang';
import { requestSchedulerCallback, cancelSchedulerCallback } from '../../utils/Timers';
import { KeyboardModeContext } from '../../../components/AppProvider/KeyboardModeProvider';
import { withTokenProps } from '../../../utils/withTokenProps';
import I18n from 'I18n';
import { ControlProvider } from '../Link/ControlContext';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { IconHolder } from '../IconHolder';
import { TRANSITION_ALL_STYLE } from '../../../constants/transition-all';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const EXPANDABLE_TEXT_HEIGHT_OFFSET = 40;
const getStyle = computedMaxHeight => ({
  maxHeight: computedMaxHeight
});

// If a rems value exists, convert it to px and return it; otherwise, return the px value.
const pxValue = (px, rems) => rems != null ? rems * 16 : px;
const TEXT_WRAPPER_BORDER_RADIUS_TOKEN = fwcBorderRadius('fwc-expandabletext-scrollable-container');
const StyledContainer = styled.div.withConfig({
  displayName: "ExpandableText__StyledContainer"
})(({
  $hasBottomToggleButton,
  $animate
}) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    transition: $animate ? TRANSITION_ALL_STYLE : 'none',
    position: !$hasBottomToggleButton ? 'relative' : 'static'
  };
});
const StyledButtonWrapper = styled.div.withConfig({
  displayName: "ExpandableText__StyledButtonWrapper"
})(({
  $buttonAlign,
  $scrollable
}) => {
  return Object.assign({
    alignSelf: $buttonAlign === 'center' ? 'center' : 'auto'
  }, $buttonAlign === 'left' && !$scrollable && {
    alignSelf: 'flex-start'
  }, $scrollable && {
    alignSelf: 'flex-end'
  });
});
const StyledTextWrapper = styled.div.withConfig({
  displayName: "ExpandableText__StyledTextWrapper"
})(({
  $hasBottomToggleButton,
  $scrollable,
  $cssStyles,
  theme
}) => {
  const computedScrollable = {
    border: `${theme.borderWidth['100']} solid ${theme.color['border-transitional-core-subtle']}`,
    borderRadius: theme.borderRadius[TEXT_WRAPPER_BORDER_RADIUS_TOKEN],
    overflowX: 'auto',
    overflowY: 'scroll',
    padding: `${theme.space['400']} ${theme.space['500']}`
  };
  return Object.assign({
    overflow: 'hidden',
    position: $hasBottomToggleButton ? 'relative' : 'static'
  }, $scrollable ? computedScrollable : {}, $cssStyles);
});
export class ExpandableTextComponent extends Component {
  constructor(props) {
    super(props);
    this._el = null;
    this._timeout = null;
    this._containerEl = null;
    this._observer = null;
    this._resizeObserver = null;
    this.delayedFitToMaxLines = () => {
      if (this._timeout) {
        cancelSchedulerCallback(this._timeout);
      }
      this._timeout = requestSchedulerCallback(this.fitToMaxLines);
    };
    this.getButtonHeight = buttonEl => {
      if (!buttonEl) return 0;
      const styles = window.getComputedStyle(buttonEl);
      const margin = parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);
      return Math.ceil(buttonEl.offsetHeight + margin);
    };
    this.fitToMaxLines = () => {
      if (!this._el) return;
      if (!this._containerEl) return;
      const {
        maxHeight = 120,
        maxHeightRems
      } = this.props;
      const {
        collapsible,
        contentHeight
      } = this.state;
      const buttonHeight = this.getButtonHeight(this._containerEl.children[1]);
      const scrollHeight = this._el.scrollHeight + buttonHeight;
      if (contentHeight !== scrollHeight) {
        this.setState({
          contentHeight: scrollHeight
        });
      }
      const contentOverflows = scrollHeight > pxValue(maxHeight, maxHeightRems);
      if (collapsible !== contentOverflows) {
        this.setState({
          collapsible: contentOverflows
        });
      }
    };
    this.handleExpandButtonClick = evt => {
      const {
        onExpandButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onExpandButtonClick, evt);
      callIfPossible(onExpandedChange, syntheticEvent(true));
    };
    this.handleCollapseButtonClick = evt => {
      const {
        onCollapseButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onCollapseButtonClick, evt);

      // Set explicit max-height (removed by handleTransitionEnd) for transition
      if (this._containerEl) {
        const textContainerEl = this._containerEl;
        textContainerEl.style.maxHeight = `${textContainerEl.scrollHeight}px`;
      }
      callIfPossible(onExpandedChange, syntheticEvent(false));
    };
    this.handleTextWrapperFocus = (evt, isKeyboardMode) => {
      const {
        expandOnFocus = true,
        onExpandedChange,
        toggleable = true
      } = this.props;
      if (expandOnFocus && toggleable) {
        // Avoid expanding on focus if the user clicked a link or other extraneous element.
        if (!isKeyboardMode && !/TEXTAREA|INPUT/.test(evt.target.tagName)) return;
        callIfPossible(onExpandedChange, syntheticEvent(true));
      }
    };
    this.handleTextWrapperTransitionEnd = evt => {
      // After the expand transition, remove max-height in case the content grows/shrinks
      const textWrapperEl = this._el;
      if (evt.target === textWrapperEl && evt.propertyName === 'max-height') {
        const {
          expanded,
          fullyExpanded
        } = this.state;
        const {
          onExpandComplete,
          onCollapseComplete
        } = this.props;
        if (expanded && !fullyExpanded) {
          this.setState({
            fullyExpanded: true
          }, () => {
            // Fire complete callback after the transition and DOM update
            callIfPossible(onExpandComplete);
          });
        } else if (!expanded && fullyExpanded) {
          // Collapsing transition completed
          callIfPossible(onCollapseComplete);
        }
      }
    };
    this.textWrapperRefCallback = ref => {
      this._el = ref;
    };
    this.textContainerRefCallback = ref => {
      this._containerEl = ref;
    };
    const {
      animate = true,
      expanded: _expanded = false
    } = props;
    this.state = {
      contentHeight: null,
      // How tall is the content?
      collapsible: false,
      // Does the content exceed our max height?
      // If animations are disabled and starting expanded, set fullyExpanded immediately
      fullyExpanded: !animate && _expanded
    };
    this._getStyle = memoizeOne(getStyle);
  }
  componentDidMount() {
    this.delayedFitToMaxLines();
    this.startMutationObserver();
    this.startResizeObserver();
    window.addEventListener('resize', this.delayedFitToMaxLines);
    if (this._el) this._el.addEventListener('load', this.delayedFitToMaxLines, true); // #5980: need to add this handler manually
    // if (!fontsLoaded()) fontsLoadedPromise.then(this.delayedFitToMaxLines);
  }
  componentDidUpdate(prevProps, prevState) {
    var _prevProps$expanded;
    this.stopMutationObserver();

    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.contentHeight === this.state.contentHeight && prevState.collapsible === this.state.collapsible) {
      this.delayedFitToMaxLines();
    }
    if (prevState.collapsible !== this.state.collapsible) {
      callIfPossible(this.props.onCollapsibleChange, this.state.collapsible);
    }

    // When animations are disabled and expanded changes, immediately set fullyExpanded
    // since transitionEnd will never fire
    const {
      animate = true,
      expanded = false,
      onExpandComplete,
      onCollapseComplete
    } = this.props;
    const prevExpanded = (_prevProps$expanded = prevProps.expanded) !== null && _prevProps$expanded !== void 0 ? _prevProps$expanded : false;
    if (!animate && expanded !== prevExpanded) {
      this.setState({
        fullyExpanded: expanded
      }, () => {
        // Fire complete callback after the DOM update and height has stabilized
        if (expanded) {
          callIfPossible(onExpandComplete);
        } else {
          callIfPossible(onCollapseComplete);
        }
      });
    }
    this.startMutationObserver();
  }
  componentWillUnmount() {
    this.stopMutationObserver();
    this.stopResizeObserver();
    if (this._timeout) {
      cancelSchedulerCallback(this._timeout);
    }
    window.removeEventListener('resize', this.delayedFitToMaxLines);
    if (this._el && this._el.removeEventListener) this._el.removeEventListener('load', this.delayedFitToMaxLines, true);
  }
  startMutationObserver() {
    if (!this._el) return;

    // When the content changes, recalculate our size
    this._observer = new MutationObserver(this.delayedFitToMaxLines);
    this._observer.observe(this._el, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  stopMutationObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }
  startResizeObserver() {
    if (!this._el) return;
    this._resizeObserver = new ResizeObserver(this.delayedFitToMaxLines);
    this._resizeObserver.observe(this._el);
  }
  stopResizeObserver() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }
  render() {
    const {
      animate = true,
      buttonAlign = 'center',
      children,
      collapseButtonText = I18n.text('salesUI.UIExpandableText.collapseButtonText'),
      cssStyles,
      domNodeRef,
      expandButtonText = I18n.text('salesUI.UIExpandableText.expandButtonText'),
      expanded = false,
      fadeOutHeight = 32,
      fadeOutHeightRems,
      gradientColor,
      hideButtonCaret = false,
      maxHeight = 120,
      maxHeightRems,
      onCollapseButtonClick,
      onExpandButtonClick,
      Overhang = DefaultOverhang,
      scrollable = false,
      toggleable = true,
      use = 'default'
    } = this.props;
    const {
      collapsible,
      contentHeight,
      fullyExpanded
    } = this.state;
    const {
      isKeyboardMode
    } = this.context;
    const showToggleButton = toggleable && collapsible;
    const hasBottomToggleButton = !scrollable && showToggleButton;
    const computedMaxHeight = expanded ? contentHeight : pxValue(maxHeight, maxHeightRems);
    const lazyEvalText = expanded ? collapseButtonText : expandButtonText;
    const renderedButton = showToggleButton ? /*#__PURE__*/_jsx(StyledButtonWrapper, {
      $buttonAlign: buttonAlign,
      $scrollable: scrollable,
      children: /*#__PURE__*/_jsxs(Button, {
        "aria-hidden": expanded ? !onCollapseButtonClick : !onExpandButtonClick,
        onClick: expanded ? this.handleCollapseButtonClick : this.handleExpandButtonClick,
        use: "link",
        marginBlock: "200",
        children: [scrollable || hideButtonCaret ? null : /*#__PURE__*/_jsx(IconHolder, {
          compact: true,
          marginInlineEnd: "200",
          icon: expanded ? UpIcon : DownIcon
        }), lazyEval(lazyEvalText)]
      })
    }) : null;
    const defaultGradientColor = use === 'on-dark' ? 'fill-surface-recessed' : 'fill-transitional-scroll-fadeout-gradient-color-1';
    return /*#__PURE__*/_jsxs(StyledContainer, {
      $hasBottomToggleButton: hasBottomToggleButton,
      $animate: animate,
      "data-collapsible": collapsible,
      ref: mergeRefs([this.textContainerRefCallback, domNodeRef]),
      style: fullyExpanded ? {} : this._getStyle(computedMaxHeight),
      children: [scrollable && renderedButton, /*#__PURE__*/_jsxs(StyledTextWrapper, {
        $hasBottomToggleButton: hasBottomToggleButton,
        $scrollable: scrollable,
        onFocus: e => this.handleTextWrapperFocus(e, isKeyboardMode),
        onTransitionEnd: this.handleTextWrapperTransitionEnd,
        ref: this.textWrapperRefCallback,
        $cssStyles: cssStyles,
        children: [children, collapsible && !expanded ? /*#__PURE__*/_jsx(Overhang, {
          gradientColor: gradientColor != null ? gradientColor : defaultGradientColor,
          insetInlineEnd: scrollable ? '25' : '0',
          insetBlockEnd: scrollable ? '25' : '0',
          insetInlineStart: scrollable ? '25' : '0',
          side: "bottom",
          size: pxValue(fadeOutHeight, fadeOutHeightRems),
          borderEndEndRadius: TEXT_WRAPPER_BORDER_RADIUS_TOKEN // Ensures that the overhang doesn't cut off the bottom of the text wrapper border
          ,
          borderEndStartRadius: TEXT_WRAPPER_BORDER_RADIUS_TOKEN // Ensures that the overhang doesn't cut off the bottom of the text wrapper border
        }) : null]
      }), /*#__PURE__*/_jsx(ControlProvider, {
        isControl: true,
        children: !scrollable && renderedButton
      })]
    });
  }
}
ExpandableTextComponent.contextType = KeyboardModeContext;
ExpandableTextComponent.defaultProps = {
  expanded: false
};
export const ExpandableText = Controllable(withTokenProps(ExpandableTextComponent), ['expanded']);
ExpandableText.displayName = 'ExpandableText';